import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShoppingCart, X } from "lucide-react";
import { API } from "../App";
import { useT } from "../lib/i18n";

export const ShoppingRun = ({ user }) => {
  const { t } = useT();
  const [activeRun, setActiveRun] = useState(null);
  const [sending, setSending] = useState(false);
  const [itemText, setItemText] = useState("");
  const [addingItem, setAddingItem] = useState(false);
  const [now, setNow] = useState(Date.now());

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("savi_token")}` });

  const fetchActive = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/shopping-run/active`, { headers: authHeaders() });
      setActiveRun(res.data || null);
    } catch {
      /* silent */
    }
  }, []);

  useEffect(() => {
    fetchActive();
    const poll = setInterval(fetchActive, 20000);
    const tick = setInterval(() => setNow(Date.now()), 15000);
    return () => {
      clearInterval(poll);
      clearInterval(tick);
    };
  }, [fetchActive]);

  const announce = async (minutes) => {
    setSending(true);
    try {
      const res = await axios.post(`${API}/shopping-run`, { minutes }, { headers: authHeaders() });
      setActiveRun(res.data);
      toast.success(`The house has been alerted — leaving in ${minutes} min 🛒`);
    } catch {
      toast.error("Couldn't send the alert. Try again?");
    }
    setSending(false);
  };

  const cancel = async () => {
    try {
      await axios.delete(`${API}/shopping-run`, { headers: authHeaders() });
      setActiveRun(null);
      toast("Shopping run ended");
    } catch {
      toast.error("Couldn't cancel");
    }
  };

  const addItem = async (e) => {
    e.preventDefault();
    const text = itemText.trim();
    if (!text || !activeRun) return;
    setAddingItem(true);
    try {
      const res = await axios.post(`${API}/shopping-run/${activeRun.id}/items`, { text }, { headers: authHeaders() });
      setActiveRun(res.data);
      setItemText("");
      toast.success("Added to the list 🛒");
    } catch {
      toast.error("Couldn't add that — the run may have ended");
      fetchActive();
    }
    setAddingItem(false);
  };

  const departsAt = activeRun ? new Date(activeRun.departs_at).getTime() : 0;
  const expiresAt = activeRun ? new Date(activeRun.expires_at || activeRun.departs_at).getTime() : 0;
  const isLive = activeRun && expiresAt > now;
  const isShopping = isLive && now >= departsAt;
  const minsLeft = isLive ? Math.max(0, Math.round((departsAt - now) / 60000)) : 0;
  const isMine = activeRun && user && activeRun.user_id === user.id;

  if (isLive) {
    const firstName = (activeRun.user_name || "Someone").split(" ")[0];
    const items = activeRun.items || [];
    return (
      <div className="rounded-xl border border-[#C85A38]/60 bg-[#C85A38]/10 p-4" data-testid="shopping-run-banner">
        <div className="flex items-start gap-3">
          <span className="relative flex h-2.5 w-2.5 mt-1.5 flex-shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#C85A38] opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#C85A38]"></span>
          </span>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-[#1A1A1A] text-sm">
              {isShopping
                ? `${isMine ? "You're" : `${firstName} is`} at the shops now!`
                : `${isMine ? "You're" : `${firstName} is`} heading to the shops ${minsLeft <= 0 ? "right now" : `in ~${minsLeft} min`}!`}
            </p>
            <p className="text-xs text-[#1A1A1A]/60 mt-0.5">
              {isMine
                ? isShopping
                  ? "Here's the house list — tap Done when you're back."
                  : "Requests from the house will appear below."
                : isShopping
                  ? "Last chance — add anything they should grab."
                  : "Need anything? Add it below before they leave."}
            </p>
          </div>
          {isMine && isShopping ? (
            <button
              onClick={cancel}
              className="flex-shrink-0 rounded-full bg-[#C85A38] text-white text-xs font-medium px-3 py-1.5"
              data-testid="shopping-run-done"
            >
              Done ✓
            </button>
          ) : (
            (isMine || user?.role === "admin") && (
              <button
                onClick={cancel}
                className="text-[#1A1A1A]/40 hover:text-[#1A1A1A] flex-shrink-0"
                data-testid="shopping-run-cancel"
                aria-label="Cancel shopping run"
              >
                <X className="w-4 h-4" />
              </button>
            )
          )}
        </div>

        {items.length > 0 && (
          <div className="mt-3 bg-white/70 rounded-xl px-3 py-2 space-y-1" data-testid="shopping-items-list">
            {items.map((item, i) => (
              <p key={item.id} className="text-xs text-[#1A1A1A]/75" data-testid={`shopping-item-${i}`}>
                • {item.text} <span className="text-[#1A1A1A]/40">— {(item.user_name || "").split(" ")[0]}</span>
              </p>
            ))}
          </div>
        )}

        <form onSubmit={addItem} className="mt-3 flex gap-2">
          <input
            type="text"
            value={itemText}
            onChange={(e) => setItemText(e.target.value)}
            maxLength={100}
            placeholder={isMine ? "Add a reminder for yourself…" : "e.g. oat milk, eggs…"}
            className="flex-1 min-w-0 rounded-xl border border-[#C85A38]/30 bg-white px-3 py-2 text-sm text-[#1A1A1A] placeholder:text-[#1A1A1A]/35 focus:outline-none focus:border-[#C85A38]"
            data-testid="shopping-item-input"
          />
          <button
            type="submit"
            disabled={addingItem || !itemText.trim()}
            className="rounded-xl bg-[#C85A38] text-white text-sm font-medium px-4 py-2 disabled:opacity-40 transition-opacity flex-shrink-0"
            data-testid="shopping-item-add"
          >
            Add
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl p-4 border border-stone-200 shadow-sm shadow-stone-200/50" data-testid="shopping-run-card">
      <div className="flex items-center gap-2.5 mb-1">
        <div className="w-8 h-8 rounded-full bg-[#C85A38]/12 flex items-center justify-center flex-shrink-0">
          <ShoppingCart className="w-4 h-4 text-[#C85A38]" strokeWidth={1.7} />
        </div>
        <h3 className="font-serif text-lg text-[#1A1A1A]">{t("Going shopping?")}</h3>
      </div>
      <p className="text-xs text-[#1A1A1A]/55 mb-3 pl-[42px]">
        {t("Alert the whole house so they can add to the list — I'm leaving in…")}
      </p>
      <div className="grid grid-cols-4 gap-2">
        {[5, 15, 30, 60].map((m) => (
          <button
            key={m}
            disabled={sending}
            onClick={() => announce(m)}
            className="rounded-lg bg-[#C85A38]/10 border border-[#C85A38]/20 text-[#C85A38] hover:bg-[#C85A38] hover:text-white font-semibold text-sm py-2.5 transition-colors active:scale-[0.96] disabled:opacity-50"
            data-testid={`shopping-run-${m}`}
          >
            {m} min
          </button>
        ))}
      </div>
    </div>
  );
};

export default ShoppingRun;
